import { computed, onBeforeUnmount, onMounted, reactive, ref, Ref } from 'vue'
import { colors } from '@/modules/tailwind'
import anime from '@/modules/animation'

export type BackgroundType = 'default' | 'secondary' | 'simple'

export interface BackgroundOptions {
  isDisabled: Ref<boolean>
  type: BackgroundType
  onClick: () => void
}

export function useFancyBackground(
  element: Ref<HTMLElement | null>,
  options: BackgroundOptions
) {
  const isAnimating = ref(false)

  const gradientValues: Record<BackgroundType, any> = {
    default: reactive({
      x: '50%',
      y: '100%',
      stop: '80%',
      fromColor: colors.purple['700'],
      toColor: colors.indigo['700']
    }),
    secondary: reactive({
      x: '50%',
      y: '50%',
      stop: '0%',
      fromColor: colors.purple['700'],
      toColor: 'transparent'
    }),
    simple: reactive({
      x: '50%',
      y: '50%',
      stop: '0%',
      fromColor: 'transparent',
      toColor: 'transparent'
    })
  }

  const backgroundStyle = computed(() => {
    const typeValues = gradientValues[options.type]
    return `radial-gradient(circle at ${typeValues.x} ${typeValues.y}, ${typeValues.fromColor}, ${typeValues.toColor} ${typeValues.stop})`
  })

  const defaultClasses = [
    'relative',
    'overflow-hidden',
    'group',
    'transition-all',
    'text-white',
    'isolate'
  ]

  const elementClasses = computed(() => {
    const classes = [...defaultClasses]

    if (options.isDisabled.value) {
      classes.push('text-gray-500', 'bg-gray-400/25', 'cursor-not-allowed')
    } else if (options.type === 'default') {
      classes.push(
        'duration-150',
        'hover:shadow-md',
        'hover:shadow-indigo-800/70'
      )
    } else if (options.type === 'secondary') {
      classes.push('border', 'border-indigo-700/80')
    } else if (options.type === 'simple') {
      classes.push('hover:bg-gray-700/30', 'duration-150')
    }

    return classes
  })

  const animationProperties: Record<BackgroundType, any> = {
    default: {
      enter: {
        targets: gradientValues.default,
        stop: '90%',
        easing: 'easeOutQuad',
        duration: 300
      },
      leave: {
        targets: gradientValues.default,
        x: '50%',
        y: '100%',
        stop: '80%',
        easing: 'easeOutQuad',
        duration: 300
      },
      click: {
        targets: gradientValues.default,
        stop: '180%',
        easing: 'easeOutQuad',
        direction: 'alternate',
        duration: 150
      }
    },
    secondary: {
      enter: {
        targets: gradientValues.secondary,
        stop: '200%',
        easing: 'easeOutQuad',
        duration: 150
      },
      leave: {
        targets: gradientValues.secondary,
        x: '50%',
        y: '50%',
        stop: '0%',
        easing: 'easeOutQuad',
        duration: 150
      },
      click: {
        targets: gradientValues.secondary,
        stop: '180%',
        easing: 'easeOutQuad',
        direction: 'alternate',
        duration: 150
      }
    },
    simple: {
      enter: {
        targets: gradientValues.simple,
        duration: 0
      },
      leave: {
        targets: gradientValues.simple,
        duration: 0
      },
      click: {
        targets: gradientValues.simple,
        duration: 0
      }
    }
  }

  const buttonEventHandlers = {
    onMouseEnter() {
      if (!options.isDisabled.value && element.value) {
        anime(animationProperties[options.type].enter)
        element.value.addEventListener(
          'mousemove',
          buttonEventHandlers.onMouseMove
        )
      }
    },
    onMouseMove(event: MouseEvent) {
      if (!options.isDisabled.value && element.value) {
        const rect = element.value.getBoundingClientRect()
        Object.assign(gradientValues[options.type], {
          x: `${((event.clientX - rect.left) / rect.width) * 100}%`,
          y: `${((event.clientY - rect.top) / rect.height) * 100}%`
        })
      }
    },
    onMouseLeave() {
      if (!options.isDisabled.value && element.value) {
        element.value.removeEventListener(
          'mousemove',
          buttonEventHandlers.onMouseMove
        )
        anime(animationProperties[options.type].leave)
      }
    },
    async onClick() {
      if (options.isDisabled.value || isAnimating.value) return
      isAnimating.value = true
      options.onClick()
      await anime(animationProperties[options.type].click).finished
      isAnimating.value = false
    }
  }

  onMounted(() => {
    if (element.value) {
      element.value.addEventListener(
        'mouseenter',
        buttonEventHandlers.onMouseEnter
      )
      element.value.addEventListener(
        'mouseleave',
        buttonEventHandlers.onMouseLeave
      )
      element.value.addEventListener('click', buttonEventHandlers.onClick)
    }
  })

  onBeforeUnmount(() => {
    if (element.value) {
      element.value.removeEventListener(
        'mouseenter',
        buttonEventHandlers.onMouseEnter
      )
      element.value.removeEventListener(
        'mousemove',
        buttonEventHandlers.onMouseMove
      )
      element.value.removeEventListener(
        'mouseleave',
        buttonEventHandlers.onMouseLeave
      )
      element.value.removeEventListener('click', buttonEventHandlers.onClick)
    }
  })

  return {
    backgroundStyle,
    elementClasses
  }
}

export function useRippleEffect(element: Ref<HTMLElement | null>) {
  const isMouseDown = ref(false)
  const isAnimating = ref(false)

  const applyRipple = async (
    el: HTMLElement,
    coordinates: { x: number; y: number }
  ) => {
    const rippleContainer = document.createElement('div')
    Object.assign(rippleContainer.style, {
      position: 'absolute',
      borderRadius: 'inherit',
      pointerEvents: 'none',
      zIndex: 0,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    })

    rippleContainer.dataset.ripple = ''

    el.prepend(rippleContainer)

    const rect = el.getBoundingClientRect()

    await anime({
      targets: rippleContainer,
      duration: 300,
      easing: 'easeOutExpo',
      update: (animation) => {
        const gradientValues = {
          x: `${(coordinates.x / rect.width) * 100}%`,
          y: `${(coordinates.y / rect.height) * 100}%`
        }
        rippleContainer.style.background = `radial-gradient(circle at ${gradientValues.x} ${gradientValues.y}, rgba(66,66,66,0.2), rgba(66,66,66,0.2) ${animation.progress * 1.3}%, transparent ${animation.progress * 1.3}%)`
      }
    }).finished

    const checkEndCondition = (resolve: (value: unknown) => void) => {
      if (isMouseDown.value) {
        setTimeout(() => {
          checkEndCondition(resolve)
        }, 150)
      } else {
        setTimeout(resolve, 150)
      }
    }

    await new Promise((resolve) => checkEndCondition(resolve))

    el.removeChild(rippleContainer)
  }

  const onMouseDown = async (event: MouseEvent) => {
    const el = element.value

    if (!el || isAnimating.value) return

    isMouseDown.value = true

    const rect = el.getBoundingClientRect()

    const clickCoordinates = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    }

    const isRelative = el.classList.contains('relative')

    if (!isRelative) el.classList.add('relative')

    const isolatedChildren: Element[] = []

    Array.from(el.children).forEach((childElement) => {
      if (!childElement.classList.contains('isolate')) {
        childElement.classList.add('isolate')
        isolatedChildren.push(childElement)
      }
    })

    isAnimating.value = true
    await applyRipple(el, clickCoordinates)

    isolatedChildren.forEach((childElement) => {
      childElement.classList.remove('isolate')
    })
    if (!isRelative) el.classList.remove('relative')

    isAnimating.value = false
  }

  const onMouseUp = () => {
    isMouseDown.value = false
  }

  onMounted(() => {
    element.value?.addEventListener('mousedown', onMouseDown)
    element.value?.addEventListener('mouseup', onMouseUp)
  })

  onBeforeUnmount(() => {
    element.value?.removeEventListener('mousedown', onMouseDown)
    element.value?.removeEventListener('mouseup', onMouseUp)
  })

  return {
    isAnimating
  }
}
